var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { memo, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import NumberFlow from '@number-flow/react';
import AddIcon from '@mui/icons-material/Add';
import { trpc } from 'root/src/client/v2/trpc/trpc';
import { LIST_QUERY_STALE_TIME_MS } from 'root/src/client/v2/common/clients/queryClient';
import { CreatorEventFulfilledDareStatus } from 'root/src/shared/dares/creatorEventFulfilledDare.types';
import { useIsAuthenticated } from 'root/src/client/v2/auth/hooks/useIsAuthenticated';
import { useIsCreator } from 'root/src/client/v2/auth/hooks/useIsCreator';
import { useIsAdmin } from 'root/src/client/v2/auth/hooks/useIsAdmin';
import { useUserId } from 'root/src/client/v2/auth/hooks/useUserId';
import { useRedesignModal } from 'root/src/client/v2/common/contexts/redesignModal.context';
import { Button } from 'root/src/client/v2/common/components/ui/button';
import { Skeleton } from 'root/src/client/v2/common/components/ui/skeleton';
import { TooltipProvider } from 'root/src/client/v2/common/components/ui/tooltip';
import { RedesignCreateDareModal } from 'root/src/client/v2/_pages/drop/redesign/RedesignCreateDareModal';
import { RedesignCommunityDareCard } from 'root/src/client/v2/_pages/drop/redesign/RedesignCommunityDareCard';
const CommunityDaresSkeleton = () => (<div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
		{Array.from({ length: 3 }).map((_, index) => (
    // eslint-disable-next-line react/no-array-index-key
    <div key={index} className="overflow-hidden rounded-2xl border border-solid border-white/[0.06] bg-card">
				<Skeleton className="aspect-[16/10] w-full rounded-none"/>
				<div className="space-y-2 p-4">
					<div className="flex items-center gap-2"><Skeleton className="h-7 w-7 rounded-full"/><Skeleton className="h-3.5 w-28"/></div>
					<Skeleton className="h-3 w-full"/>
					<Skeleton className="h-9 w-full rounded-lg"/>
				</div>
			</div>))}
	</div>);
export const RedesignCommunityDaresSection = memo(({ dropId, enableCreatorDares }) => {
    const query = trpc.dares.getDropCreatorEventFulfilledDares.useQuery({ dropId }, { enabled: Boolean(dropId), staleTime: LIST_QUERY_STALE_TIME_MS });
    const isAuthenticated = useIsAuthenticated();
    const { isCreator } = useIsCreator();
    const { isAdmin } = useIsAdmin();
    const { userId } = useUserId();
    const redesignModal = useRedesignModal();
    const refetch = () => __awaiter(void 0, void 0, void 0, function* () { yield query.refetch(); });
    const onCreateDare = () => redesignModal.show(<RedesignCreateDareModal dropId={dropId} onCreate={refetch}/>);
    const showCreateDare = isAuthenticated && isCreator && enableCreatorDares;
    const visibleDares = useMemo(() => {
        var _a;
        const dares = (_a = query.data) !== null && _a !== void 0 ? _a : [];
        return dares.filter((dare) => {
            const isDareCreator = dare.userId === userId;
            return dare.status === CreatorEventFulfilledDareStatus.Enabled || isDareCreator || isAdmin;
        });
    }, [query.data, userId, isAdmin]);
    // Nothing to show and no way to add: stay out of the layout entirely.
    if (!query.isLoading && visibleDares.length === 0 && !showCreateDare) {
        return null;
    }
    return (<section id="community-dares" className="scroll-mt-40 px-4 py-12 sm:px-6 lg:px-8">
			<div className="mx-auto max-w-7xl">
				<div className="flex items-end justify-between gap-4">
					<div>
						<h2 className="font-display text-2xl font-bold text-foreground sm:text-3xl">Community dares</h2>
						<p className="mt-1 text-sm text-muted-foreground">
							{query.isLoading ? ('Loading community dares...') : (<>
									<NumberFlow value={visibleDares.length} className="font-semibold text-foreground"/>
									{' '}{visibleDares.length === 1 ? 'dare' : 'dares'} created by creators
								</>)}
						</p>
					</div>
					{showCreateDare && (<Button variant="outline" onClick={onCreateDare} className="rounded-lg text-xs font-semibold">
							<AddIcon sx={{ fontSize: 16 }}/>
							Create a dare
						</Button>)}
				</div>

				{query.isLoading && <CommunityDaresSkeleton />}

				{!query.isLoading && visibleDares.length > 0 && (<TooltipProvider delayDuration={200}>
						<motion.div layout className="mt-6 grid auto-rows-fr gap-4 sm:grid-cols-2 lg:grid-cols-3">
							<AnimatePresence mode="popLayout">
								{visibleDares.map((dare, index) => (<motion.div key={dare.id} layout initial={{ opacity: 0, y: 20, scale: 0.95 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }} transition={{ duration: 0.3, delay: index * 0.05 }} className="h-full">
										<RedesignCommunityDareCard dare={dare} isDareCreator={dare.userId === userId} isAdmin={isAdmin} enableCreatorDares={enableCreatorDares} refetch={refetch}/>
									</motion.div>))}
							</AnimatePresence>
						</motion.div>
					</TooltipProvider>)}

				{!query.isLoading && visibleDares.length === 0 && showCreateDare && (<p className="mt-8 text-sm text-muted-foreground">No community dares yet. Be the first to create one for your audience.</p>)}
			</div>
		</section>);
});
